import { Layers, AlertCircle, PlayCircle, Eye, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { PbiUsageStats, PbiUsageFilters } from '../../types/powerBiUsage';
import { PBI_REVIEW_META } from './status';
import { fmtNum } from './format';

interface Props {
    stats: PbiUsageStats | null;
    filters: PbiUsageFilters;
    onChange: (f: PbiUsageFilters) => void;
}

type TileKey = 'all' | 'unused' | 'used' | 'undecided' | 'retire';

interface Tile {
    key: TileKey;
    label: string;
    value: number | undefined;
    icon: LucideIcon;
    color: string;
}

/**
 * Five clickable KPI cards + one info card. The NUMBERS never move when a card is
 * clicked — the stats endpoint ignores the tile filters (usage/reviewStatus/search),
 * so "12 unused of 87" stays an honest sentence while the list filters down.
 * "Reports" is the reset tile.
 */
export function PowerBiUsageStatsBar({ stats, filters, onChange }: Props) {
    const allActive = !filters.usage && !filters.reviewStatus;

    const tiles: Tile[] = [
        { key: 'all', label: 'Reports', value: stats?.totalReports, icon: Layers, color: 'text-gray-600' },
        { key: 'unused', label: 'Unused', value: stats?.unusedReports, icon: AlertCircle, color: 'text-red-600' },
        { key: 'used', label: 'Actively used', value: stats?.activeReports, icon: PlayCircle, color: 'text-green-600' },
        { key: 'undecided', label: 'Undecided', value: stats?.reviewUndecided, icon: PBI_REVIEW_META.Undecided.icon, color: PBI_REVIEW_META.Undecided.tileClass },
        { key: 'retire', label: 'Retire', value: stats?.reviewRetire, icon: PBI_REVIEW_META.Retire.icon, color: PBI_REVIEW_META.Retire.tileClass },
    ];

    const isActive = (key: TileKey): boolean => {
        switch (key) {
            case 'all': return allActive;
            case 'unused': return filters.usage === 'unused';
            case 'used': return filters.usage === 'used';
            case 'undecided': return filters.reviewStatus === 'Undecided';
            case 'retire': return filters.reviewStatus === 'Retire';
        }
    };

    const toggle = (key: TileKey) => {
        switch (key) {
            case 'all':
                onChange({ ...filters, usage: undefined, reviewStatus: undefined });
                return;
            case 'unused':
            case 'used':
                onChange({ ...filters, usage: filters.usage === key ? undefined : key });
                return;
            case 'undecided':
                onChange({ ...filters, reviewStatus: filters.reviewStatus === 'Undecided' ? undefined : 'Undecided' });
                return;
            case 'retire':
                onChange({ ...filters, reviewStatus: filters.reviewStatus === 'Retire' ? undefined : 'Retire' });
                return;
        }
    };

    return (
        <>
            {tiles.map(t => {
                const Icon = t.icon;
                const active = isActive(t.key);
                return (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => toggle(t.key)}
                        aria-pressed={active}
                        className={`w-full rounded-lg border bg-white p-3 text-left transition hover:bg-gray-50 ${
                            active ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <Icon className={`h-4 w-4 ${t.color}`} />
                            <span className={`text-xs font-medium ${active ? 'text-blue-700' : 'text-gray-500'}`}>
                                {t.label}
                            </span>
                        </div>
                        <p className="mt-1 text-xl font-bold text-gray-900">
                            {t.value === undefined ? '—' : fmtNum(t.value)}
                        </p>
                    </button>
                );
            })}
            {/* Info card — cannot filter, so it must not look pressable. */}
            <div className="w-full rounded-lg border border-gray-200 bg-gray-50 p-3 text-left">
                <div className="flex items-center gap-2">
                    <Eye className="h-4 w-4 text-blue-600" />
                    <span className="text-xs font-medium text-gray-500">Views / Users</span>
                </div>
                <p className="mt-1 text-sm font-bold text-gray-900">
                    {stats ? fmtNum(stats.viewsInWindow) : '—'} views
                </p>
                <p className="flex items-center gap-1 text-xs text-gray-500">
                    <Users className="h-3 w-3" />
                    {stats ? fmtNum(stats.distinctUsersInWindow) : '—'} users in window
                </p>
            </div>
        </>
    );
}
